import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import {
  apiFailure,
  isSelectResult,
  runQuery,
  runUpdate,
  triggerDownload,
  type ApiFailure,
  type ApiResult,
  type RowStatements,
  type SparqlBinding,
  type SparqlResult,
  type SparqlSelectResult,
  type UpdateResponse,
} from '@/lib/api';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import { GraphMultiPicker } from '@/components/GraphMultiPicker';
import { useGraphScope } from '@/lib/useGraphScope';
import { useQueryTimeoutSecs } from '@/lib/usePages';
import { RunningNotice } from '@/components/RunningNotice';
import { canImportData, canManageDashboards, isQros, useMe } from '@/lib/useMe';
import { DashboardCardModal, type CardDraft } from '@/components/DashboardCardModal';
import { SubTabs } from '@/components/SubTabs';
import { SnippetsBar } from '@/components/SnippetsBar';
import { cn, formatDecimalForDisplay } from '@/lib/utils';
import { Message, messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';
import { escapeCell } from '@/lib/tableExport';
import { TagChips } from '@/components/TagEditor';

type Mode = 'query' | 'update';

const GRAPHS_STORAGE_KEY = 'graphMultiPicker.query';
// Drafts use sessionStorage, not localStorage: each browser tab keeps its own
// in-progress text, so two tabs never fight over one draft.
const QUERY_DRAFT_STORAGE_KEY = 'queryDraft.query';
const UPDATE_DRAFT_STORAGE_KEY = 'updateDraft.query';
const MODE_STORAGE_KEY = 'mode.query';

const DEFAULT_QUERY = `SELECT ?s ?p ?o
WHERE {
  ?s ?p ?o .
}
LIMIT 10`;

// QROS speaks QQL, not SPARQL — its starter query uses FIND.
const DEFAULT_QQL_QUERY = `FIND ?s ?p ?o
WHERE {
  ?s ?p ?o
}
LIMIT 10`;

const DEFAULT_UPDATE = `INSERT DATA {
  GRAPH <urn:tres:data> {
    <http://example.org/s> <http://example.org/p> "value" .
  }
}`;

type UpdateState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'done'; result: ApiResult<UpdateResponse> & { requiresConfirmation?: boolean } };

export function QueryPage() {
  const auth = useAuth();
  const me = useMe();
  const queryClient = useQueryClient();
  // A SPARQL UPDATE is a data write: it can mint/drop a named graph and change
  // any downstream surface (Browse, Catalog, VDB/Viz, entity pages, …). Drop
  // the whole read cache via the shared helper — a hand-rolled key list here
  // used to refresh only ['graphs'] and left every other surface stale.
  const invalidateGraphsOnOk = (result: ApiResult<UpdateResponse>) => {
    if (result.ok) invalidateAfterDataWrite(queryClient);
  };
  // Restoring 'update' needs no permission gate: sessionStorage is per browser
  // tab, so the value can only exist if this user opened the Update tab here.
  const [mode, setMode] = useState<Mode>(() => {
    try {
      const saved = window.sessionStorage.getItem(MODE_STORAGE_KEY);
      return saved === 'update' ? saved : 'query';
    } catch {
      return 'query';
    }
  });
  // Storage backend from the fresh, unauthenticated /api/health probe — the
  // AUTHORITATIVE query-language signal (QROS=QQL, RDF=SPARQL). Preferred over
  // isQros(me.data) because `me` is react-query-cached (5-min staleTime) and an
  // RDF-era cache would wrongly frame a QROS instance as SPARQL (the reported
  // "only SELECT" 403). Health is refetched every mount, so it's never stale.
  const [backend, setBackend] = useState<string | undefined>();
  useEffect(() => {
    fetch('/api/health')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setBackend(d?.backend))
      .catch(() => {});
  }, []);
  // SPARQL UPDATE is unsupported on QROS (query-only — authoring goes through the
  // Ontology / Entity / Vocabulary editors), so Update mode is gated off there.
  // Trust the live health backend; fall back to the cached `me` only until the
  // health probe resolves on first paint.
  const qros = backend ? backend === 'qros' : isQros(me.data);
  const canUpdate = canImportData(me.data) && !qros;
  // Each mode keeps its own buffer so switching back doesn't clobber.
  const [queryText, setQueryText] = useState(() => {
    try {
      return window.sessionStorage.getItem(QUERY_DRAFT_STORAGE_KEY) ?? DEFAULT_QUERY;
    } catch {
      return DEFAULT_QUERY;
    }
  });
  // On a QROS backend seed the editor with QQL instead of SPARQL — but only while
  // the SPARQL default is still pristine, so we never clobber a typed query.
  useEffect(() => {
    if (qros) {
      setQueryText((prev) => (prev === DEFAULT_QUERY ? DEFAULT_QQL_QUERY : prev));
    }
  }, [qros]);
  const [updateText, setUpdateText] = useState(() => {
    try {
      return window.sessionStorage.getItem(UPDATE_DRAFT_STORAGE_KEY) ?? DEFAULT_UPDATE;
    } catch {
      return DEFAULT_UPDATE;
    }
  });
  // Empty text removes its entry so a cleared editor reseeds the default on the
  // next visit instead of restoring an empty draft.
  useEffect(() => {
    try {
      if (queryText) window.sessionStorage.setItem(QUERY_DRAFT_STORAGE_KEY, queryText);
      else window.sessionStorage.removeItem(QUERY_DRAFT_STORAGE_KEY);
      if (updateText) window.sessionStorage.setItem(UPDATE_DRAFT_STORAGE_KEY, updateText);
      else window.sessionStorage.removeItem(UPDATE_DRAFT_STORAGE_KEY);
      window.sessionStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch {
      // Storage unavailable (private mode) — drafts just won't survive navigation.
    }
  }, [queryText, updateText, mode]);

  const [running, setRunning] = useState(false);
  const [runningSince, setRunningSince] = useState<number | null>(null);
  const queryTimeout = useQueryTimeoutSecs();
  const [queryResult, setQueryResult] = useState<ApiResult<SparqlResult> | null>(null);
  // The query behind the result on screen, which "Add to dashboard" keeps; the
  // editor may have changed since the run.
  const [ranQuery, setRanQuery] = useState('');
  const [cardDraft, setCardDraft] = useState<CardDraft | null>(null);
  const [cardAdded, setCardAdded] = useState<string | null>(null);
  const [updateState, setUpdateState] = useState<UpdateState>({ kind: 'idle' });

  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  // A snippet COMPOSES into the query — at the cursor when the editor has
  // one, appended otherwise. Never a replacement ("otherwise it's not a
  // snippet"). Newlines are added only where the seam needs them.
  const insertSnippet = (content: string) => {
    const current = mode === 'query' ? queryText : updateText;
    const set = mode === 'query' ? setQueryText : setUpdateText;
    const el = editorRef.current;
    const pos = el ? el.selectionStart : current.length;
    const before = current.slice(0, pos);
    const after = current.slice(pos);
    const lead = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
    const trail = after.length > 0 && !after.startsWith('\n') ? '\n' : '';
    set(before + lead + content + trail + after);
  };

  // Graph selector is the contract (#55), extended to a SET (multi-graph
  // SELECT 2026-07-03). The /api/v1/query call sends the picked graphs as
  // `graphs`; the server attaches SPARQL-protocol dataset params
  // (default-graph-uri + named-graph-uri per graph — the desktop's SELECT
  // semantics) so the query reads from that set without the user writing
  // FROM/GRAPH themselves. Explicit GRAPH clauses in the body still take
  // precedence per the spec. "All graphs" stays the union pseudo-graph and
  // is exclusive of individual picks. (Update mode is separate — see
  // runUpdate below; SPARQL UPDATE must NOT auto-append graphs because
  // that's destructive on DELETE WHERE.)
  const [selectedGraphs, pickGraphs] = useGraphScope(GRAPHS_STORAGE_KEY);

  // Abort handle for the in-flight query fetch. Cancelling aborts the
  // browser request; the backend's Fuseki call still runs to its own
  // timeout server-side, but the page is immediately usable again.
  const abortRef = useRef<AbortController | null>(null);

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  // The persistence effect above drops the stored draft when the buffer empties.
  const handleClear = () => {
    if (mode === 'query') {
      setQueryText('');
      setQueryResult(null);
    } else {
      setUpdateText('');
      setUpdateState({ kind: 'idle' });
    }
  };

  // No graph picked means the query reads nothing (Ch08 §2.7); it does not
  // run against a default the user never chose.
  const nothingPicked = mode === 'query' && selectedGraphs.length === 0;
  const handleRun = async () => {
    if (nothingPicked) return;
    const token = auth.user?.id_token;
    // A query is a read the server grants a guest, so it goes out without a
    // token; only an update needs a signed-in one.
    if (mode === 'update' && !token) {
      setUpdateState({
        kind: 'done',
        result: apiFailure(401, 'Sign in to run an update'),
      });
      return;
    }

    setRunning(true);
    setRunningSince(Date.now());
    if (mode === 'query') {
      setQueryResult(null);
      setRanQuery(queryText);
      setCardAdded(null);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const r = await runQuery(
          {
            query: queryText,
            // QQL reads the whole store, so a scope would be silently ignored;
            // send it only where it means something.
            // The engine honors graph scope on BOTH backends (QROS since
            // 2026-08-02, raw_query pushes the selection into the match).
            // The old `!qros` guard predates that by ninety minutes and
            // silently discarded the picker on QROS (audit C1).
            ...(selectedGraphs.length > 0 ? { graphs: selectedGraphs } : {}),
          },
          token,
          controller.signal,
        );
        setQueryResult(r);
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          setQueryResult(
            apiFailure(
              0,
              'The query was cancelled. The database may keep working on it until its own time limit',
            ),
          );
        } else {
          throw e;
        }
      } finally {
        abortRef.current = null;
        setRunning(false);
        setRunningSince(null);
      }
    } else if (token) {
      setUpdateState({ kind: 'running' });
      try {
        const r = await runUpdate({ update: updateText }, token);
        setUpdateState({ kind: 'done', result: r });
        invalidateGraphsOnOk(r);
      } finally {
        setRunning(false);
        setRunningSince(null);
      }
    }
  };

  const handleConfirmRun = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    setRunning(true);
    setUpdateState({ kind: 'running' });
    try {
      const r = await runUpdate({ update: updateText, confirmed: true }, token);
      setUpdateState({ kind: 'done', result: r });
      invalidateGraphsOnOk(r);
    } finally {
      setRunning(false);
    }
  };

  const buttonLabel = (() => {
    if (running) return mode === 'query' ? 'Running…' : 'Updating…';
    return mode === 'query' ? 'Run' : 'Update';
  })();

  return (
    <div className="container mx-auto py-8">
      <SubTabs
        tabs={[
          { to: '/query', label: 'Query' },
          { to: '/browse', label: 'Browse', page: 'browse' },
          { to: '/snippets', label: 'Snippets', page: 'snippets' },
        ]}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Query</h1>
        {/* Query is a power surface: the picker stays visible on every
            backend, and the selection reaches the engine — `raw_query` narrows
            QQL reads to the selected graphs (pinned by
            raw_query_narrows_to_the_selected_graphs). */}
        <GraphMultiPicker selected={selectedGraphs} onChange={pickGraphs} />
      </div>
      {nothingPicked && (
        <p className="mt-3 text-sm text-muted-foreground">
          No graph picked, so the query reads nothing. Pick{' '}
          <span className="font-medium">All graphs</span> or any set of named graphs above.
        </p>
      )}

      {/* Mode tabs — Update only renders for users with ImportData. */}
      <div className="mt-4 flex gap-2 border-b border-border">
        <ModeTab active={mode === 'query'} onClick={() => setMode('query')}>
          Query
        </ModeTab>
        {canUpdate && (
          <ModeTab active={mode === 'update'} onClick={() => setMode('update')}>
            Update
          </ModeTab>
        )}
      </div>

      {qros && canImportData(me.data) && (
        <p className="mt-2 text-xs text-muted-foreground">
          QROS is query-only — author data through the Ontology, Entity, and Vocabulary
          editors.
        </p>
      )}

      <p className="mt-3 text-sm text-muted-foreground">
        {mode === 'query' ? (
          qros ? (
            <>
              QQL — FIND patterns over the QROS store.
              {selectedGraphs.length > 0 && !selectedGraphs.includes(ALL_GRAPHS) ? (
                <>
                  {' '}This query reads{' '}
                  {selectedGraphs.map((g, i) => (
                    <span key={g}>
                      {i > 0 && ', '}
                      <code className="text-xs">{g}</code>
                    </span>
                  ))}
                  .
                </>
              ) : (
                <> This query reads the whole store.</>
              )}
            </>
          ) : (
            <>
              SELECT, CONSTRUCT, ASK, and DESCRIBE.
              {selectedGraphs.length > 0 ? (
                <>
                  {' '}Query will read from{' '}
                  {selectedGraphs.includes(ALL_GRAPHS) ? (
                    <span className="font-medium">all graphs (union)</span>
                  ) : (
                    selectedGraphs.map((g, i) => (
                      <span key={g}>
                        {i > 0 && ', '}
                        <code className="text-xs">{g}</code>
                      </span>
                    ))
                  )}
                  . Explicit <code className="text-xs">GRAPH &lt;…&gt; {'{ … }'}</code> clauses
                  in your query still override the picker for their own scope.
                </>
              ) : (
                <>
                  {' '}No graph picked, so the query reads only the unnamed default graph —
                  empty under the managed-graph methodology. Pick{' '}
                  <span className="font-medium">All graphs</span> or any set of named graphs
                  to widen the scope.
                </>
              )}
            </>
          )
        ) : (
          <>
            INSERT, DELETE, INSERT DATA, DELETE DATA, DELETE WHERE. Risky operations
            (unrestricted DELETE WHERE, multiple INSERT DATA) require explicit
            confirmation.
          </>
        )}
      </p>

      <SnippetsBar
        currentText={mode === 'query' ? queryText : updateText}
        onInsert={insertSnippet}
      />

      <div className="mt-4">
        <label className="block">
          <span className="text-sm font-medium">
            {mode === 'query' ? 'Query' : 'Update'}
          </span>
          <textarea
            ref={editorRef}
            value={mode === 'query' ? queryText : updateText}
            onChange={(e) =>
              mode === 'query' ? setQueryText(e.target.value) : setUpdateText(e.target.value)
            }
            disabled={running}
            spellCheck={false}
            rows={10}
            className={cn(
              'mt-1 block w-full rounded-md border border-input bg-background px-3 py-2',
              'font-mono text-sm leading-relaxed',
              'focus:outline-none focus:ring-2 focus:ring-ring',
              'disabled:opacity-50',
            )}
          />
        </label>

        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={handleRun}
            disabled={running || nothingPicked}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-medium text-primary-foreground',
              mode === 'update'
                ? 'bg-tres-warning hover:bg-tres-warning/90'
                : 'bg-primary hover:bg-primary/90',
              'disabled:opacity-50',
            )}
          >
            {buttonLabel}
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={running || (mode === 'query' ? !queryText : !updateText)}
            className={cn(
              'rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-tres-text-primary',
              'hover:bg-muted disabled:opacity-50',
            )}
          >
            Clear
          </button>
          {running && mode === 'query' && (
            <button
              type="button"
              onClick={handleCancel}
              className={buttonVariants({ variant: 'danger', size: 'lg' })}
            >
              Cancel
            </button>
          )}
          {running && (
            <RunningNotice
              since={runningSince}
              budgetSecs={mode === 'query' ? (queryTimeout.data ?? undefined) : undefined}
            />
          )}
          <span className="text-xs text-muted-foreground">
            {mode === 'query' ? (
              <>
                POST /api/v1/query
                {backend === 'qros' ? ' · QQL' : backend === 'rdf' ? ' · query' : ''}
              </>
            ) : (
              <>POST /api/v1/update — VDB rebuild auto-scheduled on success</>
            )}
          </span>
        </div>
      </div>

      <div className="mt-8">
        {mode === 'query' ? (
          <QueryResultPanel
            result={queryResult}
            {...(canManageDashboards(me.data)
              ? {
                  addToDashboard: {
                    outcome: cardAdded,
                    onClick: (data: SparqlSelectResult) =>
                      setCardDraft({
                        title: '',
                        kind: cardKindOf(data),
                        sparql: ranQuery,
                        source: 'Built from the query you ran.',
                      }),
                  },
                }
              : {})}
          />
        ) : (
          <UpdateResultPanel
            state={updateState}
            onConfirm={handleConfirmRun}
            onCancel={() => setUpdateState({ kind: 'idle' })}
            running={running}
          />
        )}
      </div>

      <DashboardCardModal
        open={cardDraft !== null}
        draft={cardDraft ?? undefined}
        onClose={() => setCardDraft(null)}
        onSaved={({ title, dashboard }) => {
          setCardAdded(`“${title}” is on the ${dashboard} dashboard.`);
          void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        }}
      />
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-foreground text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// Page-size choices mirror the desktop Query tab's result pagination.
const PAGE_SIZES = [25, 50, 100, 200, 500] as const;

/** A 400 is the user's own text to correct, so the heading says that and the
 *  detail says what to change; any other status is a failure and keeps its
 *  status and name. */
function QueryFailure({ what, failure }: { what: 'query' | 'update'; failure: ApiFailure }) {
  const correctable = failure.status === 400;
  const reason = correctable ? (failure.detail ?? failure.error) : undefined;
  return (
    <Message kind="warning" className="p-4">
      <div className="font-medium">
        {correctable
          ? `This ${what} was not run. Correct it and run it again.`
          : failure.message}
      </div>
      {reason && <div className="mt-1 whitespace-pre-wrap">{reason}</div>}
    </Message>
  );
}

/** What a card made from a result shows: one whole number is a count;
 *  anything else is a table, which the card dialog lets the user change. */
function cardKindOf(data: SparqlSelectResult): CardDraft['kind'] {
  const rows = data.results.bindings;
  const only = data.head.vars.length === 1 ? data.head.vars[0] : undefined;
  const value = only !== undefined && rows.length === 1 ? rows[0]?.[only]?.value : undefined;
  return value !== undefined && /^\d+$/.test(value) ? 'count' : 'table';
}

/** A row's statements with every tag on them (QQL TAG-8): each tagged
 *  statement in words, subject, property and object, with its tags under it,
 *  then how many more the row stands for. */
function RowTags({ row }: { row: RowStatements | undefined }) {
  if (!row) return null;
  const tagged = row.statements.filter((st) => st.tags.length > 0);
  return (
    <div className="space-y-1">
      {tagged.map((st, i) => (
        <div key={i}>
          <div className="text-tres-text-primary">
            {st.subjectLabel}{' '}
            <span className="text-tres-text-secondary">{st.predicateLabel}</span>{' '}
            {st.objectLabel}
          </div>
          <TagChips tags={st.tags} />
        </div>
      ))}
      {row.more > 0 && (
        <div className="text-tres-text-secondary">
          and {row.more} more {row.more === 1 ? 'statement' : 'statements'}
        </div>
      )}
    </div>
  );
}

export function QueryResultPanel({
  result,
  addToDashboard,
}: {
  result: ApiResult<SparqlResult> | null;
  /** Offered to a user who manages dashboards: the result's query as a card. */
  addToDashboard?: { onClick: (data: SparqlSelectResult) => void; outcome: string | null };
}) {
  const [pageSize, setPageSize] = useState<number>(100);
  const [page, setPage] = useState(0);
  // New result: snap back to the first page.
  useEffect(() => {
    setPage(0);
  }, [result]);

  if (!result) return null;

  if (!result.ok) {
    return (
      <QueryFailure what="query" failure={result} />
    );
  }

  const data = result.data;
  const warningsBanner = result.warnings && result.warnings.length > 0 && (
    <div className={cn(messageClass('platform', 'compact', 'warning'), 'mb-3')}>
      <span className="font-medium">Query advisories:</span> {result.warnings.join(' · ')}
    </div>
  );

  if (!isSelectResult(data)) {
    return (
      <div>
        {warningsBanner}
        <div className="rounded-md border border-border bg-card p-4 text-sm">
          <div className="text-muted-foreground">ASK result</div>
          <div className="mt-1 text-2xl font-medium">{String(data.boolean)}</div>
        </div>
      </div>
    );
  }

  const vars = data.head.vars;
  const bindings = data.results.bindings;
  const support = data.support;
  const supportLine = support && (
    <span
      title={
        support.subjects.length > 0
          ? support.subjects.slice(0, 20).join('\n') +
            (support.subjectsTruncated || support.subjects.length > 20 ? '\n…' : '')
          : undefined
      }
    >
      basis: {support.statementCount}{' '}
      {support.statementCount === 1 ? 'statement' : 'statements'} in scope
      {support.asOf ? ` · as of ${support.asOf}` : ''}
    </span>
  );

  const planBlock = data.plan && (
    <div className="rounded-md border border-border bg-card p-4 text-sm">
      <div className="text-muted-foreground">Query plan, in the order the patterns run</div>
      <pre className="mt-2 whitespace-pre-wrap font-mono text-xs">{data.plan}</pre>
    </div>
  );

  if (bindings.length === 0) {
    return (
      <div>
        {warningsBanner}
        {planBlock || (
          <div className="rounded-md border border-border bg-card p-4 text-sm text-muted-foreground">
            No results.
            {supportLine && <div className="mt-1 text-xs">{supportLine}</div>}
          </div>
        )}
      </div>
    );
  }

  const pageCount = Math.ceil(bindings.length / pageSize);
  const clampedPage = Math.min(page, pageCount - 1);
  const visible = bindings.slice(clampedPage * pageSize, (clampedPage + 1) * pageSize);
  // QQL TAG-8: every tag on the statements a row matched, unasked. The column
  // appears when any row's statements carry a tag.
  const rowStatements = data.rowStatements;
  const showTags =
    rowStatements?.some((r) => r.more > 0 || r.statements.some((st) => st.tags.length > 0)) ?? false;

  return (
    <div>
    {warningsBanner}
    {planBlock && <div className="mb-3">{planBlock}</div>}
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="min-w-full text-sm">
        <thead className="border-b border-border bg-muted/50">
          <tr>
            {vars.map((v) => (
              <th key={v} className="px-3 py-2 text-left font-medium">
                {v}
              </th>
            ))}
            {showTags && (
              <th className="px-3 py-2 text-left font-medium">Statements and their tags</th>
            )}
          </tr>
        </thead>
        <tbody>
          {visible.map((b, i) => (
            <tr key={clampedPage * pageSize + i} className="border-b border-border last:border-0">
              {vars.map((v) => (
                <td key={v} className="px-3 py-2 align-top font-mono text-xs">
                  {b[v]?.type === 'uri' ? (
                    // An identifier opens its entity page, the way back to a thing the
                    // label search cannot reach, such as one with no type (77.12).
                    <Link
                      to={`/entity?uri=${encodeURIComponent(b[v].value)}`}
                      title={`Open ${b[v].value}`}
                      className="text-tres-action underline-offset-2 hover:underline"
                    >
                      {b[v].value}
                    </Link>
                  ) : (
                    formatDecimalForDisplay(b[v]?.value ?? '')
                  )}
                </td>
              ))}
              {showTags && (
                <td className="px-3 py-2 align-top text-xs">
                  <RowTags row={rowStatements?.[clampedPage * pageSize + i]} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-3 border-t border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <span>
          {bindings.length} {bindings.length === 1 ? 'row' : 'rows'}
        </span>
        {supportLine}
        {pageCount > 1 && (
          <span className="flex items-center gap-1">
            <button
              type="button"
              disabled={clampedPage === 0}
              onClick={() => setPage(clampedPage - 1)}
              className="rounded border border-input px-1.5 disabled:opacity-40"
            >
              Prev
            </button>
            <span>
              page {clampedPage + 1} / {pageCount}
            </span>
            <button
              type="button"
              disabled={clampedPage >= pageCount - 1}
              onClick={() => setPage(clampedPage + 1)}
              className="rounded border border-input px-1.5 disabled:opacity-40"
            >
              Next
            </button>
          </span>
        )}
        <label className="flex items-center gap-1">
          <span>per page</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            className="rounded border border-input bg-background px-1 py-0.5"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        {addToDashboard && (
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => addToDashboard.onClick(data)}
              title="Make this query a card on the Dashboard, counted again each time the Dashboard opens"
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              Add to dashboard
            </button>
            {addToDashboard.outcome && (
              <span role="status" className="text-tres-success">
                {addToDashboard.outcome}
              </span>
            )}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          <span>Download:</span>
          <DownloadButton onClick={() => downloadSelectResults(data, 'csv')}>CSV</DownloadButton>
          <DownloadButton onClick={() => downloadSelectResults(data, 'tsv')}>TSV</DownloadButton>
          <DownloadButton onClick={() => downloadSelectResults(data, 'json')}>JSON</DownloadButton>
          <DownloadButton onClick={() => downloadSelectResults(data, 'xml')}>XML</DownloadButton>
        </span>
      </div>
    </div>
    </div>
  );
}

function DownloadButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={buttonVariants({ variant: 'secondary', size: 'sm' })}
    >
      {children}
    </button>
  );
}

/**
 * Convert an already-fetched SPARQL SELECT result into the requested
 * format and trigger a browser download. Client-side conversion is
 * free for tabular formats; we never re-query the backend.
 *
 * - **CSV/TSV**: RFC 4180-style for CSV, plain TSV otherwise. Cell
 *   values are pulled from `binding.value` (the unquoted lexical
 *   form). URI vs literal vs bnode distinction is dropped at the
 *   tabular boundary — matches what tools like LibreOffice / Excel
 *   expect, and the column header is the variable name so the user
 *   can re-infer.
 * - **JSON**: the raw SPARQL Results envelope, pretty-printed.
 * - **XML**: W3C SPARQL Query Results XML Format
 *   (https://www.w3.org/TR/rdf-sparql-XMLres/) — preserves URI/literal/
 *   bnode distinction plus xml:lang and datatype attributes.
 */
function downloadSelectResults(
  data: SparqlSelectResult,
  format: 'csv' | 'tsv' | 'json' | 'xml',
) {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  if (format === 'json') {
    triggerDownload(
      JSON.stringify(data, null, 2),
      `query-results-${stamp}.json`,
      'application/sparql-results+json',
    );
    return;
  }
  if (format === 'xml') {
    triggerDownload(
      sparqlResultsToXml(data),
      `query-results-${stamp}.xml`,
      'application/sparql-results+xml',
    );
    return;
  }
  const sep = format === 'csv' ? ',' : '\t';
  const lines: string[] = [];
  lines.push(data.head.vars.map((v) => escapeCell(v, sep)).join(sep));
  for (const row of data.results.bindings) {
    lines.push(
      data.head.vars
        .map((v) => escapeCell(bindingToCellText(row[v]), sep))
        .join(sep),
    );
  }
  triggerDownload(
    lines.join('\n') + '\n',
    `query-results-${stamp}.${format}`,
    format === 'csv' ? 'text/csv;charset=utf-8' : 'text/tab-separated-values;charset=utf-8',
  );
}

/**
 * Emit SPARQL Query Results XML Format. The element model is the W3C
 * spec's: <sparql><head><variable name="..."/></head><results><result>
 * <binding name="..."><uri|literal|bnode>...</...></binding></result></results></sparql>.
 * Literal bindings carry xml:lang or datatype attributes when present.
 */
function sparqlResultsToXml(data: SparqlSelectResult): string {
  const esc = (s: string) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  const lines: string[] = [];
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');
  lines.push('<sparql xmlns="http://www.w3.org/2005/sparql-results#">');
  lines.push('  <head>');
  for (const v of data.head.vars) {
    lines.push(`    <variable name="${esc(v)}"/>`);
  }
  lines.push('  </head>');
  lines.push('  <results>');
  for (const row of data.results.bindings) {
    lines.push('    <result>');
    for (const v of data.head.vars) {
      const b = row[v];
      if (!b) continue;
      const name = esc(v);
      const val = esc(b.value);
      if (b.type === 'uri') {
        lines.push(`      <binding name="${name}"><uri>${val}</uri></binding>`);
      } else if (b.type === 'bnode') {
        lines.push(`      <binding name="${name}"><bnode>${val}</bnode></binding>`);
      } else {
        // 'literal' (W3C calls typed-literal 'literal' too in JSON results
        // form; the datatype attribute distinguishes them in XML).
        let attrs = '';
        if (b['xml:lang']) attrs += ` xml:lang="${esc(b['xml:lang'])}"`;
        if (b.datatype) attrs += ` datatype="${esc(b.datatype)}"`;
        lines.push(`      <binding name="${name}"><literal${attrs}>${val}</literal></binding>`);
      }
    }
    lines.push('    </result>');
  }
  lines.push('  </results>');
  lines.push('</sparql>');
  return lines.join('\n') + '\n';
}

function bindingToCellText(b: SparqlBinding | undefined): string {
  if (!b) return '';
  return b.value;
}

function UpdateResultPanel({
  state,
  onConfirm,
  onCancel,
  running,
}: {
  state: UpdateState;
  onConfirm: () => void;
  onCancel: () => void;
  running: boolean;
}) {
  if (state.kind === 'idle') return null;
  if (state.kind === 'running') {
    return (
      <div className="rounded-md border border-border bg-card p-4 text-sm text-muted-foreground">
        Running update…
      </div>
    );
  }

  const { result } = state;

  // Confirmation gate: backend flagged the update as risky and is
  // asking for an explicit re-submit. Render a prominent inline prompt
  // with both a Confirm and a Cancel action so the user has agency —
  // missing the button was the #61 report.
  if (!result.ok && result.requiresConfirmation) {
    return (
      <div className={messageClass('platform', 'normal', 'warning')}>
        <div className="text-base font-semibold text-tres-warning">
          ⚠ Confirmation required to run this update
        </div>
        <div className="mt-2 text-tres-text-primary">This update changes data in a way that needs your confirmation before it runs.</div>
        {result.detail && (
          <div className={cn(messageClass('platform', 'compact', 'warning'), 'mt-3 whitespace-pre-wrap font-mono')}>
            {result.detail}
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={running}
            className={cn(
              'rounded-md bg-tres-warning px-4 py-2 text-sm font-semibold text-white shadow-sm',
              'hover:bg-tres-warning/90 disabled:opacity-50',
            )}
          >
            Confirm and run
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={running}
            className={cn(
              'rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-tres-text-primary',
              'hover:bg-muted disabled:opacity-50',
            )}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!result.ok) {
    return (
      <QueryFailure what="update" failure={result} />
    );
  }

  return (
    <div className="rounded-md border border-tres-success bg-tres-success/10 p-4 text-sm">
      <div className="font-medium text-tres-success">Update succeeded</div>
      <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 font-mono text-xs">
        <dt className="text-tres-text-secondary">type</dt>
        <dd>{result.data.query_type}</dd>
        <dt className="text-tres-text-secondary">duration_ms</dt>
        <dd>{result.data.duration_ms.toLocaleString()}</dd>
        <dt className="text-tres-text-secondary">vdb_rebuild</dt>
        <dd>
          {result.data.vdb_rebuild_scheduled
            ? 'scheduled'
            : 'already in flight (will pick up new data)'}
        </dd>
      </dl>
    </div>
  );
}

